import React, { useEffect, useState } from 'react';

import {
  externalCalibrationService,
  pdfService,
  serialNumbersService,
} from 'services';

import './DocumentsOverview.scss';
import { ROUTES } from '../../constants/routes';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { CustomButton } from '../CustomButton';
import { Spinner } from '../Spinner';
import { ExternalCalibration } from '../../interfaces';
import { SerialNumber } from '../../interfaces/serialNumber';
import { AxiosResponse } from 'axios';

const formatDisplayDate = (date?: string) =>
  date ? dayjs(date).format('DD MMMM YYYY') : ' - ';

const formatDisplayValue = (value?: string | number | null) =>
  value !== undefined && value !== null && value !== '' ? String(value) : ' - ';

export const DocumentsOverview = () => {
  const { sn, certId } = useParams();
  const navigate = useNavigate();

  const [isCalError, setIsCalError] = useState<boolean>(false);
  const [isCalLoading, setIsCalLoading] = useState<boolean>(true);
  const [isSerialError, setIsSerialError] = useState<boolean>(false);

  const [noPdfs, setNoPdfs] = useState<Record<string, boolean>>({});
  const [invalids, setInvalids] = useState<Record<string, boolean>>({});
  const [pendings, setPendings] = useState<Record<string, boolean>>({});

  const [cal, setValues] = useState<ExternalCalibration>(
    {} as ExternalCalibration
  );
  const [serial, setSerial] = useState<SerialNumber>({} as SerialNumber);

  const certificateTypes: Array<{
    key: string;
    title: string;
    info: (serial: SerialNumber, cal?: ExternalCalibration) => JSX.Element;
    available: (serial: SerialNumber, cal?: ExternalCalibration) => boolean;
    invalid?: (serial: SerialNumber, cal?: ExternalCalibration) => boolean;
    alwaysEdit?: boolean;
    downloadMethod: (
      serial: SerialNumber,
      cal?: ExternalCalibration
    ) => Promise<AxiosResponse>;
    suffix: string;
    editRoute?: string;
    editParam?: (
      serial: SerialNumber,
      cal?: ExternalCalibration
    ) => string | number | undefined;
  }> = [
    {
      key: 'original',
      title: 'Original calibration certificate',
      info: (serial) => (
        <>
          <div>
            Date of calibration: {formatDisplayDate(serial.pruefdatum)}
          </div>
          <div>
            Commissioning Date: {formatDisplayDate(serial.commissioning_date)}
          </div>
        </>
      ),
      available: (serial) => !noPdfs.original,
      invalid: (serial) => invalids.original,
      alwaysEdit: true,
      downloadMethod: (serial) => pdfService.originalCert(serial.sn),
      suffix: '_original.pdf',
      editRoute: ROUTES.edit_original,
      editParam: (serial) => serial.sn,
    },
    {
      key: 'authorized',
      title: 'Authorized calibration check certificate',
      info: (serial, cal) => {
        if (
          isCalError ||
          invalids.authorized ||
          !cal?.sn ||
          !cal?.certificateNr
        ) {
          return (
            <div>
              Please note that authorized external calibration checks performed
              before 1 January 2024 are not available in the database. To obtain
              the latest calibration check certificate from a date prior to 1
              January 2024, please contact our authorized service partner,
              accordingly.
            </div>
          );
        } else {
          return (
            <>
              <div>
                Date of calibration:{' '}
                {dayjs(cal.dateOfCalibration).format('DD MMMM YYYY')}
              </div>
              <div>Place of calibration: {cal.placeOfCalibration}</div>
              <div>Technician: {cal.technician}</div>
            </>
          );
        }
      },
      available: (serial, cal) => !noPdfs.authorized && !isCalLoading,
      invalid: (serial, cal) =>
        isCalError || invalids.authorized || !cal?.sn || !cal?.certificateNr,
      downloadMethod: (serial, cal) =>
        pdfService.authorizedCert(cal!.certificateNr, cal!.sn),
      suffix: '_authorized.pdf',
      editRoute: ROUTES.edit_authorized,
      editParam: (serial, cal) => cal?.certificateNr,
    },
    {
      key: 'worksTest',
      title: 'Works test certificate',
      info: (serial) => <div>Alarm Test Point set to {serial.alarmpunkt}</div>,
      available: (serial) => !noPdfs.worksTest,
      downloadMethod: (serial) => pdfService.worksTestCert(serial.sn),
      suffix: '_works_test.pdf',
    },
    {
      key: 'typeApproval',
      title: 'Type Approval',
      info: (serial) => (
        <div>
          Date of production:{' '}
          {dayjs(serial.produktionsdatum).format('DD MMMM YYYY')}
        </div>
      ),
      available: (serial) => !noPdfs.typeApproval,
      downloadMethod: (serial) => pdfService.typeApprovalCert(serial.sn),
      suffix: '_type_approval.pdf',
    },
    {
      key: 'modulB',
      title: 'Modul B',
      info: () => <div>EC Type Examination Certificate</div>,
      available: (serial) => !noPdfs.modulB,
      downloadMethod: (serial) => pdfService.modulB(serial.sn),
      suffix: '_modul_b.pdf',
    },
    {
      key: 'modulD',
      title: 'Modul D',
      info: () => <div>Quality Assurance System Certificate</div>,
      available: (serial) => !noPdfs.modulD,
      downloadMethod: (serial) => pdfService.modulD(serial.sn),
      suffix: '_modul_d.pdf',
    },
    {
      key: 'dnv',
      title: 'DNV Clean Design',
      info: (serial) => <div>Alarm Test Point set to {serial.alarmpunkt}</div>,
      available: (serial) => !noPdfs.dnv,
      downloadMethod: (serial) => pdfService.dnvCert(serial.sn),
      suffix: '_dnv.pdf',
    },
    {
      key: 'doc',
      title: 'Declaration of Conformity',
      info: (serial) => (
        <div>
          Date of production:{' '}
          {dayjs(serial.produktionsdatum).format('DD MMMM YYYY')}
        </div>
      ),
      available: (serial) => !noPdfs.doc,
      downloadMethod: (serial) => pdfService.docCert(serial.sn),
      suffix: '_doc.pdf',
    },
    {
      key: 'uscg',
      title: 'USCG',
      info: () => <div>United States Coast Guard Approval</div>,
      available: (serial) => !noPdfs.uscg,
      downloadMethod: (serial) => pdfService.uscgCert(serial.sn),
      suffix: '_uscg.pdf',
    },
    {
      key: 'docUk',
      title: 'DoC UKCA',
      info: () => <div>UK Declaration of conformity</div>,
      available: (serial) => !noPdfs.docUk,
      downloadMethod: (serial) => pdfService.docUkCert(serial.sn),
      suffix: '_doc_uk.pdf',
    },
    {
      key: 'modulBUkca',
      title: 'Modul B UKCA',
      info: () => <div>UK Type Examination Certificate</div>,
      available: (serial) => !noPdfs.modulBUkca,
      downloadMethod: (serial) => pdfService.modulBUkca(serial.sn),
      suffix: '_modul_b_ukca.pdf',
    },
    {
      key: 'modulDUkca',
      title: 'Modul D UKCA',
      info: () => <div>UK Certificate of Conformity</div>,
      available: (serial) => !noPdfs.modulDUkca,
      downloadMethod: (serial) => pdfService.modulDUkca(serial.sn),
      suffix: '_modul_d_ukca.pdf',
    },
    {
      key: 'ccs',
      title: 'CCS',
      info: () => <div>China Classification Society Certificate</div>,
      available: (serial) => !noPdfs.ccs,
      downloadMethod: (serial) => pdfService.ccsCert(serial.sn),
      suffix: '_ccs.pdf',
    },
  ];

  useEffect(() => {
    (async () => {
      try {
        if (certId) {
          let response;
          try {
            response = await externalCalibrationService.findByCertificateNumber(
              certId
            );
          } catch (e) {
            if (sn)
              response = await externalCalibrationService.findBySerialNumber(
                sn
              );
            else setIsCalError(true);
          }

          setValues(response);
        } else if (sn) {
          const response = await externalCalibrationService.findBySerialNumber(
            sn
          );

          setValues(response);
        }
      } catch (e) {
        setIsCalError(true);
      } finally {
        setIsCalLoading(false);
      }
    })();
  }, [sn, certId]);

  useEffect(() => {
    (async () => {
      try {
        if (sn) {
          const response = await serialNumbersService.findSerial(sn);
          setSerial(response);
          setNoPdfs({
            original: !response.originalPdfAvailable,
            authorized: !response.authorizedPdfAvailable,
            worksTest: !response.worksTestPdfAvailable,
            typeApproval: !response.typeApprovalPdfAvailable,
            doc: !response.docPdfAvailable,
            ccs: !response.ccsPdfAvailable,
            docUk: !response.docUkPdfAvailable,
            dnv: !response.dnvPdfAvailable,
            uscg: !response.uscgPdfAvailable,
            modulB: !response.modulBPdfAvailable,
            modulD: !response.modulDPdfAvailable,
            modulBUkca: !response.modulBUkcaPdfAvailable,
            modulDUkca: !response.modulDUkcaPdfAvailable,
          });
          setInvalids({
            original: !response.originalPdfValid,
            authorized: !response.authorizedPdfValid,
          });
        }
      } catch (e) {
        setIsSerialError(true);
      }
    })();
  }, [sn, certId]);

  const downloadCert = async (key: string) => {
    const type = certificateTypes.find((t) => t.key === key);
    if (!type) return;
    setPendings((prev) => ({ ...prev, [key]: true }));
    try {
      const response = await type.downloadMethod(serial, cal);
      triggerDownload(response, type.suffix);
    } finally {
      setPendings((prev) => ({ ...prev, [key]: false }));
    }
  };

  function triggerDownload(response: AxiosResponse, suffix: string) {
    const fileURL = window.URL.createObjectURL(response as unknown as Blob);
    const alink = document.createElement('a');
    alink.href = fileURL;
    alink.download = serial.sn + suffix;
    alink.click();
  }

  return (
    <>
      {isSerialError && (
        <div className="documents-overview">
          <h1>Serial number: {sn}</h1>
          <div className="info">
            <h3>Serial number not found</h3>
            <div>
              An error occurred while trying to find the serial number.
              <br />
              <br />
              Please check the serial number and try again.
            </div>
          </div>
        </div>
      )}

      {!isSerialError && (
        <div className="documents-overview">
          <div className="serial-header">
            <h1>Serial number: {serial.sn}</h1>
            {serial.typ !== 'Computer' && (
              <div className="serial-info">
                <div>
                  Validity: {formatDisplayDate(serial.lastCertificateValidity)}
                </div>
                <div>
                  Technical Expiry Date: {formatDisplayDate(serial.expire_date)}
                </div>
                <div>Vessel Name: {formatDisplayValue(serial.schiffsname)}</div>
                <div>IMO Number: {formatDisplayValue(serial.imo_nr)}</div>
              </div>
            )}
          </div>

          {certificateTypes.map((type) => {
            if (!type.available(serial, cal) || !serial.sn) return null;
            const isInvalid = type.invalid?.(serial, cal);
            return (
              <div className="calibration" key={type.key}>
                <div className="info">
                  <h3>{type.title}</h3>
                  <div className="info-lines">{type.info(serial, cal)}</div>
                </div>
                <div className="actions">
                  {type.editRoute && (!isInvalid || type.alwaysEdit) && (
                    <CustomButton
                      text="Edit calibration"
                      disabled={!serial.sn}
                      onClick={() =>
                        navigate(
                          `${type.editRoute}/${type.editParam!(serial, cal)}`
                        )
                      }
                    />
                  )}
                  {!isInvalid && (
                    <div className="download-action">
                      <div className="spinner-box">
                        {pendings[type.key] && <Spinner />}
                      </div>
                      <CustomButton
                        text="Download PDF"
                        onClick={() => downloadCert(type.key)}
                      />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};
